import SearchInput from '@/components/form/SearchInput'
import { ActionSurface } from '@/components/patterns/action-surface'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { type InfiniteData, useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { History, LayoutGrid, List, PackageOpen, Plus, ShoppingCart } from 'lucide-react'
import { useDebounce } from '@uidotdev/usehooks'
import { type KeyboardEvent, useDeferredValue, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { type CartItemDetailsFormValues, CustomItemModal } from './CustomItemModal'
import {
  CategoryGridSlot,
  CategoryTileDragOverlay,
  DraggableHorizontalScrollArea,
  POSCategoryManager,
  SortableCategoryTile,
} from './POSCategoryShortcuts'
import { ShortcutResultTile } from './ShortcutResultTile'
import { VariantSelectionModal } from './VariantSelectionModal'
import { usePageBarcodeScanner } from '@/hooks/useBarcodeScanner'
import { usePageSearchShortcut } from '@/hooks/usePageSearchShortcut'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  type CatalogAddResult,
  posCatalogProductFilters,
  type PosCartActions,
} from '@/lib/pos/cart-actions'
import queryClient from '@/lib/queryClient'
import {
  categoryQueryKeys,
  useCategories,
  useUpdateCategoryMutation,
  useUpdatePosCategoryOrderMutation,
} from '@/lib/services/categories/categories.query'
import type { CreatedCart, CustomCartItemInput, PosCart } from '@/lib/services/carts/carts.types'
import type { Category, CategoryPosColor } from '@/lib/services/categories/categories.types'
import { collectionQueries } from '@/lib/services/collections/collections.query'
import type {
  Collection,
  CollectionIndexFilters,
  CollectionIndexResult,
} from '@/lib/services/collections/collections.types'
import { useCustomerPosHistoryItems } from '@/lib/services/customers/customers.query'
import type { CustomerPosHistoryItem } from '@/lib/services/customers/customers.types'
import { productQueries, useInfiniteProducts } from '@/lib/services/products/products.query'
import type {
  Product,
  ProductIndexFilters,
  ProductIndexResult,
  Variant,
} from '@/lib/services/products/products.types'
import {
  addTrailingCategoryDropSlots,
  type CategorySlotId,
  getCategorySlotId,
  getCategorySlotIds,
  getCategorySlotIndexFromId,
  getCategorySortOrder,
  getCustomItemDefaultsFromCategory,
  getCustomItemDefaultsFromHistoryItem,
  getRememberedQuickAddCategoryPrice,
  rememberQuickAddCategoryPrice,
  sortPosCategories,
  trimTrailingEmptyCategorySlots,
  updateCategoryQueryData,
} from '@/lib/pos/category-shortcuts'
import {
  getPosCartQuantityByVariantId,
  getPosVariantUnitPrice,
  isPosProductOutOfStock,
  type PosQuantityByVariantId,
} from '@/lib/pos/inventory'
import { lookupScannedCode } from '@/lib/pos/scanned-code'
import { cn, formatCurrency, getErrorMessage } from '@/lib/utils'

export const POS_PRODUCT_CATALOG_PAGE_SIZE = 20
// Product search waits for this pause in typing, so it doesn't ask the API on every keystroke.
export const POS_PRODUCT_SEARCH_DEBOUNCE_MS = 300
const CATEGORY_GRID_DRAG_EMPTY_SLOTS = 8
const POS_PRODUCT_GRID_GAP_PX = 8
const POS_PRODUCT_CARD_TARGET_WIDTH_PX = 160
const POS_PRODUCT_ROW_ESTIMATED_HEIGHT_PX = 232
const SUBSCRIPTION_PACKAGE_CATALOG_TYPE = 'subscription_package'

function getProductsFromPage(page: ProductIndexResult): Product[] {
  return Array.isArray(page) ? page : (page?.data ?? [])
}

function getProductsFromInfiniteData(
  data: InfiniteData<ProductIndexResult> | undefined
): Product[] {
  return data?.pages?.flatMap(getProductsFromPage) ?? []
}

function asArray(data: CollectionIndexResult | undefined): Collection[] {
  return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []
}

function getProductGridColumnCount(width: number): number {
  if (!width) {
    return 1
  }

  return Math.max(
    1,
    Math.floor(
      (width + POS_PRODUCT_GRID_GAP_PX) /
        (POS_PRODUCT_CARD_TARGET_WIDTH_PX + POS_PRODUCT_GRID_GAP_PX)
    )
  )
}

function useElementWidth(element: HTMLElement | null): number {
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!element) {
      return undefined
    }

    const updateWidth = () => {
      const nextWidth = element.getBoundingClientRect().width || element.clientWidth || 0

      setWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth))
    }

    updateWidth()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth)

      return () => window.removeEventListener('resize', updateWidth)
    }

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [element])

  return width
}

function getDefaultVariant(product: Product): Variant | null {
  return product.variants?.find((variant) => variant.is_default) || product.variants?.[0] || null
}

function isSubscriptionPackageProduct(product: Product): boolean {
  return product?.product_catalog_type === SUBSCRIPTION_PACKAGE_CATALOG_TYPE
}

function getDefaultSubscriptionFrequency(product: Product) {
  const frequencyOptions = Array.isArray(product?.subscription_frequency_options)
    ? product.subscription_frequency_options
    : []

  return frequencyOptions.includes('monthly') ? 'monthly' : (frequencyOptions[0] ?? null)
}

function getMinPrice(product: Product): number | null {
  if (!product.variants || product.variants.length === 0) {
    return null
  }

  const prices = product.variants
    .map((variant) => Number.parseFloat(getPosVariantUnitPrice(variant)))
    .filter((price) => !Number.isNaN(price))

  return prices.length > 0 ? Math.min(...prices) : null
}

function ProductCatalogCard({
  product,
  disabled,
  quantityByVariantId,
  onClick,
}: {
  product: Product
  disabled: boolean
  quantityByVariantId: PosQuantityByVariantId
  onClick: (product: Product) => void
}) {
  const variant = getDefaultVariant(product)
  const image = product.media?.[0] || variant?.media
  const variantCount = product.variants?.length || 0
  const hasMultipleVariants = variantCount > 1
  const isOutOfStock = isPosProductOutOfStock(product, quantityByVariantId)
  const isProductDisabled = disabled || isOutOfStock
  const minPrice = getMinPrice(product)

  return (
    <ActionSurface
      padding="compact"
      onClick={() => onClick(product)}
      disabled={isProductDisabled}
      className="group w-full flex-col items-center gap-0"
    >
      <div className="relative mb-2 aspect-square w-full overflow-hidden rounded-lg bg-muted">
        {image ? (
          <img
            src={image.url}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className={cn(
              'h-full w-full object-cover transition-transform',
              !isProductDisabled && 'group-hover:scale-105'
            )}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ShoppingCart className="h-8 w-8" />
          </div>
        )}
        {variantCount > 1 && (
          <Badge variant="secondary" size="xs" className="absolute right-2 top-2">
            {variantCount}
          </Badge>
        )}
      </div>
      <Typography variant="label" align="center" className="line-clamp-2 w-full">
        {product.name}
      </Typography>
      {minPrice !== null && (
        <Typography variant="labelStrong" tone="muted" className="mt-1">
          {hasMultipleVariants ? 'from ' : ''}
          {formatCurrency(minPrice)}
        </Typography>
      )}
      {isOutOfStock && (
        <Typography variant="label" tone="destructive" className="mt-2">
          Out of stock
        </Typography>
      )}
    </ActionSurface>
  )
}

function VirtualizedProductGrid({
  disabled,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onProductClick,
  products,
  quantityByVariantId,
  resetKey,
  scrollViewportElement,
}: {
  disabled: boolean
  fetchNextPage: ReturnType<typeof useInfiniteProducts>['fetchNextPage']
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onProductClick: (product: Product) => void
  products: Product[]
  quantityByVariantId: PosQuantityByVariantId
  resetKey: string
  scrollViewportElement: HTMLDivElement | null
}) {
  const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null)
  const gridWidth = useElementWidth(gridElement)
  const columnCount = getProductGridColumnCount(gridWidth)
  const productRowCount = Math.ceil(products.length / columnCount)
  const hasLoaderRow = hasNextPage || isFetchingNextPage
  const virtualRowCount = productRowCount + (hasLoaderRow ? 1 : 0)
  const viewportWidth = scrollViewportElement?.clientWidth ?? gridWidth
  const viewportHeight =
    scrollViewportElement?.clientHeight ?? POS_PRODUCT_ROW_ESTIMATED_HEIGHT_PX * 3
  // TanStack Virtual owns scroll measurement state; React Compiler safely skips this hook.
  // eslint-disable-next-line react-hooks/incompatible-library
  const rowVirtualizer = useVirtualizer({
    count: virtualRowCount,
    enabled: Boolean(scrollViewportElement),
    getScrollElement: () => scrollViewportElement,
    initialRect: {
      width: viewportWidth || POS_PRODUCT_CARD_TARGET_WIDTH_PX,
      height: viewportHeight || POS_PRODUCT_ROW_ESTIMATED_HEIGHT_PX,
    },
    estimateSize: () => POS_PRODUCT_ROW_ESTIMATED_HEIGHT_PX,
    overscan: 4,
    useFlushSync: false,
  })
  const virtualRows = rowVirtualizer.getVirtualItems()
  const lastVirtualRowIndex = virtualRows.at(-1)?.index ?? -1

  useEffect(() => {
    rowVirtualizer.scrollToOffset(0)
  }, [resetKey, rowVirtualizer])

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || lastVirtualRowIndex < productRowCount - 1) {
      return
    }

    void fetchNextPage({ cancelRefetch: false })
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, lastVirtualRowIndex, productRowCount])

  return (
    <div ref={setGridElement} className="w-full min-w-0" data-testid="pos-product-virtual-grid">
      <div
        className="relative w-full min-w-0"
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          contain: 'layout style paint',
        }}
      >
        {virtualRows.map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= productRowCount
          const rowProducts = products.slice(
            virtualRow.index * columnCount,
            virtualRow.index * columnCount + columnCount
          )

          return (
            <div
              key={virtualRow.key}
              ref={rowVirtualizer.measureElement}
              data-index={virtualRow.index}
              className="absolute left-0 top-0 w-full pb-2"
              style={{
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <div
                  data-testid="pos-product-catalog-load-more"
                  className="flex h-8 items-center justify-center px-3 text-center"
                >
                  {isFetchingNextPage ? (
                    <Typography variant="label" tone="muted">
                      Loading more products...
                    </Typography>
                  ) : null}
                </div>
              ) : (
                <div
                  className="grid justify-start gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${columnCount}, minmax(8.5rem, 10rem))`,
                  }}
                >
                  {rowProducts.map((product) => (
                    <ProductCatalogCard
                      key={product.id}
                      product={product}
                      disabled={disabled}
                      quantityByVariantId={quantityByVariantId}
                      onClick={onProductClick}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CustomerHistoryQuickAdd({
  cart,
  disabled,
  onSelectHistoryItem,
}: {
  cart: PosCart | null
  disabled: boolean
  onSelectHistoryItem: (item: CustomerPosHistoryItem) => void
}) {
  const customerId = cart?.purchaser_id
  const { data: historyItems = [], isFetching } = useCustomerPosHistoryItems(customerId, {
    enabled: Boolean(customerId),
    limit: 8,
  })

  if (!customerId) {
    return null
  }

  if (!isFetching && historyItems.length === 0) {
    return null
  }

  return (
    <div className="min-w-0 max-w-full overflow-hidden border-t pt-4">
      <div className="mb-2 flex items-center gap-2 text-muted-foreground">
        <History className="h-4 w-4" />
        <Typography variant="label">Previously ordered</Typography>
      </div>
      <DraggableHorizontalScrollArea variant="history">
        {historyItems.map((item) => {
          // The order snapshot's image is loosely shaped; only a string url shows.
          const imageUrl = item.image?.url

          return (
            <ActionSurface
              key={item.key}
              padding="sm"
              disabled={disabled}
              onClick={() => onSelectHistoryItem(item)}
              className="min-w-40 max-w-52 shrink-0 items-center gap-2"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
                {typeof imageUrl === 'string' && imageUrl ? (
                  <img src={imageUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <PackageOpen className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0">
                <Typography variant="label" className="block truncate">
                  {item.name}
                </Typography>
                <Typography variant="label" tone="muted" className="block truncate">
                  {formatCurrency(item.price)}
                </Typography>
              </div>
            </ActionSurface>
          )
        })}
      </DraggableHorizontalScrollArea>
    </div>
  )
}

export function ProductCatalog({
  cart,
  cartId = null,
  cartActions,
  createCartWithDefaults,
  disabled = false,
  className,
}: {
  cart: PosCart | null
  /** The cart to add to while `cart` hasn't loaded. */
  cartId?: number | null
  cartActions: PosCartActions
  createCartWithDefaults: () => Promise<CreatedCart>
  disabled?: boolean
  className?: string
}) {
  const organizationId = useRouteOrganizationId()
  const [activeTab, setActiveTab] = useState('categories')
  const [searchQuery, setSearchQuery] = useState('')
  // A scanned code shared by several products lists exactly those until the search is edited.
  const [exactCodeSearch, setExactCodeSearch] = useState<string | null>(null)
  const trimmedSearchQuery = useDeferredValue(searchQuery).trim()
  // While the search has text, one list of matching shortcuts and products replaces the tabs.
  const isSearching = trimmedSearchQuery !== '' || exactCodeSearch !== null
  // Shortcuts match as the florist types. Products always search the text in the box, but only
  // once it has stayed the same for a moment, so typing doesn't ask the API on every keystroke.
  const isSearchTextSettled =
    useDebounce(trimmedSearchQuery, POS_PRODUCT_SEARCH_DEBOUNCE_MS) === trimmedSearchQuery
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null)
  const { data: categories } = useCategories({ filters: { sort: 'pos' } })
  const posCategories = sortPosCategories(
    (categories ?? []).filter((category) => category.show_in_pos)
  )
  const effectiveActiveTab =
    activeTab === 'categories' && posCategories.length === 0 ? 'products' : activeTab
  const updateSearchQuery = (value: string) => {
    setExactCodeSearch(null)
    setSearchQuery(value)
  }
  const showTab = (tab: string) => {
    updateSearchQuery('')
    setActiveTab(tab)
  }
  // Search looks through every product, best matches first; collections only narrow browsing.
  const catalogFilters: ProductIndexFilters = {
    ...posCatalogProductFilters,
    ...(exactCodeSearch
      ? { code: exactCodeSearch }
      : isSearching
        ? { search: trimmedSearchQuery, sort: 'relevance' }
        : selectedCollectionId
          ? { collection_id: selectedCollectionId }
          : {}),
  }
  const [productScrollViewportElement, setProductScrollViewportElement] =
    useState<HTMLDivElement | null>(null)
  const {
    data: productPages,
    fetchNextPage,
    hasNextPage = false,
    isError: isProductsError,
    isFetchingNextPage,
    isPending: isProductsPending,
  } = useInfiniteProducts({
    filters: catalogFilters,
    pageSize: POS_PRODUCT_CATALOG_PAGE_SIZE,
    enabled: isSearching
      ? exactCodeSearch !== null || isSearchTextSettled
      : effectiveActiveTab === 'products',
    refetchOnWindowFocus: true,
  })
  const collectionFilters: CollectionIndexFilters = {
    include: ['media'],
    with_active_products_count: true,
    with_storefront_products_count: true,
  }
  const { data: collectionsData } = useQuery({
    ...collectionQueries.index({
      organizationId,
      filters: collectionFilters,
    }),
    enabled: !isSearching && effectiveActiveTab === 'products',
  })
  const collections = asArray(collectionsData)
  const products = getProductsFromInfiniteData(productPages)
  const productGridResetKey = isSearching
    ? `search:${exactCodeSearch ?? trimmedSearchQuery}`
    : `collection:${selectedCollectionId ?? 'all'}`
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false)
  const [isCustomItemModalOpen, setIsCustomItemModalOpen] = useState(false)
  const [customItemDefaults, setCustomItemDefaults] =
    useState<Partial<CartItemDetailsFormValues> | null>(null)
  const [quickAddCategoryIdForPriceMemory, setQuickAddCategoryIdForPriceMemory] = useState<
    number | null
  >(null)
  const [categorySlotIds, setCategorySlotIds] = useState<CategorySlotId[]>([])
  const [activeCategoryDragId, setActiveCategoryDragId] = useState<UniqueIdentifier | null>(null)
  const [activeCategoryDragSize, setActiveCategoryDragSize] = useState<{
    width: number
    height: number
  } | null>(null)
  const isSavingCategoryReorderRef = useRef(false)
  const updatePosCategoryOrderMutation = useUpdatePosCategoryOrderMutation({
    invalidateOnSuccess: false,
  })
  const updateCategoryMutation = useUpdateCategoryMutation({ invalidateOnSuccess: false })
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  // An order change may reuse the stock its order already holds.
  const quantityByVariantId = getPosCartQuantityByVariantId(
    cart?.cart_items,
    cart?.order_change?.held_units
  )
  const persistedCartId = cartId ?? cart?.id ?? null
  // How many times each code waiting for lookup was scanned, in the order first heard, and whether
  // a lookup is running.
  const pendingScansRef = useRef(new Map<string, number>())
  const isAddingScansRef = useRef(false)

  useEffect(() => {
    if (isSavingCategoryReorderRef.current) {
      // Keep the optimistic grid stable until the atomic reorder save has finished.
      return
    }

    setCategorySlotIds(
      getCategorySlotIds((categories ?? []).filter((category) => category.show_in_pos))
    )
  }, [categories])

  const baseCategorySlotIds =
    categorySlotIds.length > 0 ? categorySlotIds : getCategorySlotIds(posCategories)
  const renderedCategorySlotIds = activeCategoryDragId
    ? addTrailingCategoryDropSlots(baseCategorySlotIds, CATEGORY_GRID_DRAG_EMPTY_SLOTS)
    : baseCategorySlotIds
  const categoryById = new Map(posCategories.map((category) => [Number(category.id), category]))
  // A scanned code shared by several products lists only those products to choose from.
  const matchingShortcuts =
    exactCodeSearch === null && trimmedSearchQuery !== ''
      ? posCategories.filter((category) =>
          category.name.toLowerCase().includes(trimmedSearchQuery.toLowerCase())
        )
      : []
  const visibleCategoryGridSlots = renderedCategorySlotIds.map((id, slotIndex) => ({
    category: id ? categoryById.get(Number(id)) : null,
    slotIndex,
    isPersistedGap: !id && slotIndex < baseCategorySlotIds.length,
  }))
  const hasVisibleCategoryTiles = visibleCategoryGridSlots.some((slot) => slot.category)
  const activeCategoryDrag = activeCategoryDragId
    ? (categoryById.get(Number(activeCategoryDragId)) ?? null)
    : null
  const selectedProduct =
    selectedProductId !== null
      ? (products.find((product) => product.id === selectedProductId) ?? null)
      : null

  // Shows the add in the cart at once; the cart actions save it after the changes ahead of it.
  const addToCart = (
    product: Product,
    variant: Variant,
    {
      quantity = 1,
      subscriptionFrequency = null,
      isVariantCurrent = false,
      onSaved,
      onFailed,
    }: {
      quantity?: number
      subscriptionFrequency?: string | null
      isVariantCurrent?: boolean
      onSaved?: (quantity: number) => void
      onFailed?: () => void
    } = {}
  ): CatalogAddResult | null => {
    const isSubscriptionPackage = isSubscriptionPackageProduct(product)
    const frequency = isSubscriptionPackage
      ? (subscriptionFrequency ?? getDefaultSubscriptionFrequency(product))
      : null

    if (isSubscriptionPackage && !frequency) {
      toast.error('Failed to add item', {
        description: 'Choose an available subscription frequency.',
      })
      return null
    }

    return cartActions.addCatalogItem({
      organizationId,
      cartId: persistedCartId,
      createCart: createCartWithDefaults,
      product,
      variant,
      quantity,
      subscriptionFrequency: frequency,
      isVariantCurrent,
      onSaved,
      onFailed,
    })
  }

  const handleProductClick = (product: Product) => {
    if (!product.variants || product.variants.length === 0) return

    const isOutOfStock = isPosProductOutOfStock(product, quantityByVariantId)
    if (isOutOfStock) return

    const [onlyVariant] = product.variants
    if (!isSubscriptionPackageProduct(product) && product.variants.length === 1 && onlyVariant) {
      addToCart(product, onlyVariant)
      return
    }

    setSelectedProductId(product.id)
    setIsVariantModalOpen(true)
  }

  // Scans aren't added while the order is being paid for, finalized, or cleared, as clicks aren't.
  const skipScanWhileOrderEnds = (code: string) => {
    if (!disabled) {
      return false
    }

    toast.warning(`We didn't add ${code}. Scan it again once this order finishes.`)
    return true
  }

  const addScannedCode = async (code: string, count: number) => {
    try {
      const match = await lookupScannedCode({
        organizationId,
        code,
        filters: posCatalogProductFilters,
      })

      if (match.kind === 'none') {
        toast.warning(`We couldn't find a product with barcode or SKU ${code}.`, {
          description: "Add it to a product's barcodes to scan it here.",
        })
        return
      }

      if (match.kind === 'several') {
        setSearchQuery(code)
        setExactCodeSearch(code)
        toast.info(`More than one product uses ${code}. Choose one below.`)
        return
      }

      const { product, variant } = match.option
      const optionName = (product.variants?.length ?? 0) > 1 ? ` (${variant.name})` : ''
      const result = addToCart(product, variant, {
        quantity: count,
        isVariantCurrent: true,
        onSaved: (quantity) => {
          const countLabel = quantity > 1 ? ` ×${quantity}` : ''
          toast.success(`Added ${product.name}${optionName}${countLabel}`, { id: 'pos-scan' })
        },
      })

      if (result?.status === 'out-of-stock') {
        toast.warning(`${product.name} is out of stock.`)
      } else if (result && result.quantity < count) {
        toast.warning(`Only ${result.quantity} of ${product.name} left in stock.`)
      }
    } catch (error) {
      toast.error('Failed to look up that code', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  // Looks up scans one code at a time. Repeats of a code heard meanwhile join its count, so a
  // burst of the same item looks up once and adds together.
  const addPendingScans = async () => {
    isAddingScansRef.current = true

    try {
      // A Map's iteration also reaches codes added while it runs, in the order first heard.
      for (const [code, count] of pendingScansRef.current) {
        pendingScansRef.current.delete(code)
        await addScannedCode(code, count)
      }
    } finally {
      isAddingScansRef.current = false
    }
  }

  const handleScannedCode = (code: string) => {
    if (skipScanWhileOrderEnds(code)) {
      return
    }

    pendingScansRef.current.set(code, (pendingScansRef.current.get(code) ?? 0) + 1)

    // The cart stays busy until the scans are queued, so nothing ends the order under them.
    if (!isAddingScansRef.current) {
      void cartActions.track(addPendingScans())
    }
  }

  // Scans add only outside text fields; a code typed or scanned into search stays a search.
  usePageBarcodeScanner({ onScan: (code) => handleScannedCode(code) })
  usePageSearchShortcut(searchInputRef)

  // Esc clears the search, then leaves the box so scans add again.
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') {
      return
    }

    if (searchQuery) {
      updateSearchQuery('')
    } else {
      event.currentTarget.blur()
    }
  }

  const handleVariantSelected = (
    variant: Variant,
    options: { subscriptionFrequency?: string } = {}
  ) => {
    // The modal offers options only while it shows a product.
    if (!selectedProduct || addToCart(selectedProduct, variant, options)?.status !== 'queued') {
      return
    }

    setIsVariantModalOpen(false)
    setSelectedProductId(null)
  }

  const handleCategoryQuickAdd = (category: Category) => {
    setQuickAddCategoryIdForPriceMemory(category?.id ?? null)
    setCustomItemDefaults({
      ...getCustomItemDefaultsFromCategory(category),
      price: getRememberedQuickAddCategoryPrice({
        orgId: organizationId,
        categoryId: category?.id,
      }),
    })
    setIsCustomItemModalOpen(true)
  }

  const openHistoryItemForReview = (item: CustomerPosHistoryItem) => {
    setQuickAddCategoryIdForPriceMemory(null)
    setCustomItemDefaults(getCustomItemDefaultsFromHistoryItem(item))
    setIsCustomItemModalOpen(true)
  }

  const handleCustomItemModalOpenChange = (isOpen: boolean) => {
    setIsCustomItemModalOpen(isOpen)

    if (!isOpen) {
      setQuickAddCategoryIdForPriceMemory(null)
    }
  }

  const handleCustomItemAdded = ({ customItem }: { customItem: CustomCartItemInput }) => {
    if (!quickAddCategoryIdForPriceMemory) {
      return
    }

    rememberQuickAddCategoryPrice({
      orgId: organizationId,
      categoryId: quickAddCategoryIdForPriceMemory,
      price: customItem?.price,
    })
  }

  const handleHistoryItemClick = async (item: CustomerPosHistoryItem) => {
    if (item.kind === 'catalog' && item.product_id && item.variant_id) {
      try {
        const product = await cartActions.track(
          queryClient.fetchQuery({
            ...productQueries.show({
              organizationId,
              productId: item.product_id,
              filters: posCatalogProductFilters,
            }),
            staleTime: 0,
          })
        )
        const variant = product?.variants?.find((candidate) => candidate.id === item.variant_id)

        if (!variant) {
          throw new Error('That catalog item is no longer available.')
        }

        const result = addToCart(product, variant, {
          isVariantCurrent: true,
          onFailed: () => openHistoryItemForReview(item),
        })

        if (result?.status !== 'queued') {
          openHistoryItemForReview(item)
        }
      } catch (error) {
        toast.warning('Review previous item', {
          description:
            getErrorMessage(error) ||
            'That catalog item could not be matched to a current product.',
        })
        openHistoryItemForReview(item)
      }

      return
    }

    openHistoryItemForReview(item)
  }

  const clearCategoryDragState = () => {
    setActiveCategoryDragId(null)
    setActiveCategoryDragSize(null)
  }

  const handleCategoryColorChange = async (category: Category, posColor: CategoryPosColor) => {
    const categoryQueryKey = categoryQueryKeys.all({ organizationId })
    const previousCategorySnapshots =
      queryClient.getQueriesData?.({
        queryKey: categoryQueryKey,
      }) ?? []

    queryClient.setQueriesData?.(
      {
        queryKey: categoryQueryKey,
      },
      (data) => updateCategoryQueryData(data, category.id, { pos_color: posColor })
    )

    try {
      await updateCategoryMutation.mutateAsync({
        id: category.id,
        pos_color: posColor,
      })
    } catch (error) {
      previousCategorySnapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData?.(queryKey, data)
      })
      void queryClient.invalidateQueries?.({
        queryKey: categoryQueryKey,
      })
      toast.error('Failed to update shortcut color', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    }
  }

  const handleCategoryDragStart = ({ active }: DragStartEvent) => {
    setActiveCategoryDragId(active?.id ?? null)

    const initialRect = active?.rect?.current?.initial
    setActiveCategoryDragSize(
      initialRect
        ? {
            width: initialRect.width,
            height: initialRect.height,
          }
        : null
    )
  }

  const handleCategoryDragEnd = async ({ active, over }: DragEndEvent) => {
    clearCategoryDragState()

    if (!over || active.id === over.id) {
      return
    }

    const currentSlotIds = addTrailingCategoryDropSlots(
      categorySlotIds.length > 0 ? categorySlotIds : getCategorySlotIds(posCategories),
      CATEGORY_GRID_DRAG_EMPTY_SLOTS
    )
    const activeCategoryId = Number(active.id)
    const oldSlotIndex = currentSlotIds.findIndex((id) => Number(id) === Number(activeCategoryId))
    const droppedSlotIndex = getCategorySlotIndexFromId(over.id)
    const overCategorySlotIndex = currentSlotIds.findIndex((id) => String(id) === String(over.id))
    const newSlotIndex = droppedSlotIndex ?? overCategorySlotIndex

    if (oldSlotIndex < 0 || newSlotIndex < 0 || oldSlotIndex === newSlotIndex) {
      return
    }

    let nextSlotIds = [...currentSlotIds]

    if (nextSlotIds[newSlotIndex]) {
      const occupiedSlotIndexes = nextSlotIds.flatMap((id, slotIndex) => (id ? [slotIndex] : []))
      const occupiedIds = nextSlotIds.flatMap((id) => (id ? [id] : []))
      const oldOccupiedIndex = occupiedIds.findIndex((id) => Number(id) === activeCategoryId)
      const newOccupiedIndex = occupiedSlotIndexes.findIndex(
        (slotIndex) => slotIndex === newSlotIndex
      )

      if (oldOccupiedIndex < 0 || newOccupiedIndex < 0) {
        return
      }

      const knownOccupiedIds = new Set(occupiedIds.map((id) => Number(id)))
      const reorderedOccupiedIds = arrayMove(occupiedIds, oldOccupiedIndex, newOccupiedIndex)
      const normalizedReorderedOccupiedIds = reorderedOccupiedIds.filter((id) =>
        knownOccupiedIds.has(Number(id))
      )
      occupiedIds.forEach((id) => {
        if (!normalizedReorderedOccupiedIds.some((candidate) => Number(candidate) === Number(id))) {
          normalizedReorderedOccupiedIds.push(id)
        }
      })
      nextSlotIds = nextSlotIds.map((id) => (id ? null : id))
      occupiedSlotIndexes.forEach((slotIndex, index) => {
        // Both lists hold one entry per occupied slot.
        nextSlotIds[slotIndex] = normalizedReorderedOccupiedIds[index] ?? null
      })
    } else {
      nextSlotIds[oldSlotIndex] = null
      nextSlotIds[newSlotIndex] = activeCategoryId
    }

    nextSlotIds = trimTrailingEmptyCategorySlots(nextSlotIds)

    const categoryReorderUpdates = nextSlotIds.flatMap((id, slotIndex) => {
      const category = posCategories.find((candidate) => Number(candidate.id) === Number(id))

      return category && getCategorySortOrder(category, slotIndex) !== slotIndex
        ? [{ category, slotIndex }]
        : []
    })
    const categoryQueryKey = categoryQueryKeys.all({ organizationId })
    const previousCategorySnapshots =
      queryClient.getQueriesData?.({
        queryKey: categoryQueryKey,
      }) ?? []

    isSavingCategoryReorderRef.current = true
    setCategorySlotIds(nextSlotIds)

    if (categoryReorderUpdates.length > 0) {
      queryClient.setQueriesData?.(
        {
          queryKey: categoryQueryKey,
        },
        (data) =>
          categoryReorderUpdates.reduce(
            (updatedData, { category, slotIndex }) =>
              updateCategoryQueryData(updatedData, category.id, {
                show_in_pos: true,
                pos_sort_order: slotIndex,
              }),
            data
          )
      )
    }

    try {
      if (categoryReorderUpdates.length > 0) {
        await updatePosCategoryOrderMutation.mutateAsync({
          categories: categoryReorderUpdates.map(({ category, slotIndex }) => ({
            id: category.id,
            show_in_pos: true,
            pos_sort_order: slotIndex,
          })),
        })
      }
    } catch (error) {
      previousCategorySnapshots.forEach(([queryKey, data]) => {
        queryClient.setQueryData?.(queryKey, data)
      })
      setCategorySlotIds(getCategorySlotIds(posCategories))
      toast.error('Failed to reorder quick-add shortcuts', {
        description: getErrorMessage(error) || 'Please try again.',
      })
    } finally {
      isSavingCategoryReorderRef.current = false
    }
  }

  const renderProductResults = (emptyMessage: string) =>
    isProductsPending ? (
      <div className="flex h-full min-h-[200px] items-center justify-center text-muted-foreground">
        <Typography variant="body">Loading products...</Typography>
      </div>
    ) : isProductsError ? (
      <div className="flex h-full min-h-[200px] items-center justify-center text-muted-foreground">
        <Typography variant="body">Unable to load products.</Typography>
      </div>
    ) : products.length === 0 ? (
      <div className="flex h-full min-h-[200px] items-center justify-center text-muted-foreground">
        <Typography variant="body">{emptyMessage}</Typography>
      </div>
    ) : (
      <VirtualizedProductGrid
        disabled={disabled}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onProductClick={handleProductClick}
        products={products}
        quantityByVariantId={quantityByVariantId}
        resetKey={productGridResetKey}
        scrollViewportElement={productScrollViewportElement}
      />
    )

  return (
    <Card
      className={cn(
        '@container flex flex-col gap-0 lg:h-full lg:min-h-0 lg:overflow-hidden',
        className
      )}
    >
      <CardHeader className="shrink-0 space-y-3">
        <SearchInput
          inputRef={searchInputRef}
          value={searchQuery}
          setValue={updateSearchQuery}
          onKeyDown={handleSearchKeyDown}
          placeholder="Press / to search shortcuts and products"
          aria-label="Search shortcuts and products"
          aria-keyshortcuts="/"
          className="w-full"
        />
        {/* No tab is selected while searching, so either tab leaves the search. Tabs then switch
            only when chosen, so tabbing past them to the results keeps the search. */}
        <Tabs
          value={isSearching ? '' : effectiveActiveTab}
          onValueChange={showTab}
          activationMode={isSearching ? 'manual' : 'automatic'}
          className="gap-3"
          size="compact"
        >
          <div className="flex flex-col gap-3 @[28rem]:flex-row @[28rem]:items-center @[28rem]:justify-between">
            <TabsList aria-label="Choose products to add" className="no-scrollbar">
              <TabsTrigger value="categories">
                <LayoutGrid className="h-4 w-4" />
                Shortcuts
              </TabsTrigger>
              <TabsTrigger value="products">
                <List className="h-4 w-4" />
                All Website Products
              </TabsTrigger>
            </TabsList>
            <POSCategoryManager
              categories={categories ?? []}
              disabled={disabled}
              orgId={organizationId}
            />
          </div>
        </Tabs>
      </CardHeader>

      <CardContent className="min-h-0 min-w-0 flex-1 overflow-hidden p-0">
        {isSearching ? (
          <ScrollArea
            className="h-full min-h-0 min-w-0 max-w-full overflow-hidden"
            viewportRef={setProductScrollViewportElement}
            viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
            viewportStyle={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
          >
            <div className="w-full min-w-0 max-w-full space-y-4 overflow-hidden p-4">
              {/* One row of smaller tiles keeps the product grid near the top, which its scrolling expects. */}
              {matchingShortcuts.length > 0 ? (
                <section aria-label="Matching shortcuts" className="space-y-2">
                  <Typography variant="labelStrong" className="block">
                    Shortcuts
                  </Typography>
                  <DraggableHorizontalScrollArea>
                    {matchingShortcuts.map((category) => (
                      <ShortcutResultTile
                        key={category.id}
                        category={category}
                        disabled={disabled}
                        onAdd={handleCategoryQuickAdd}
                      />
                    ))}
                  </DraggableHorizontalScrollArea>
                </section>
              ) : null}
              <section aria-label="Matching website products" className="space-y-2">
                <Typography variant="labelStrong" className="block">
                  Website products
                </Typography>
                {renderProductResults(
                  matchingShortcuts.length > 0
                    ? 'No website products match.'
                    : `No shortcuts or products match “${exactCodeSearch ?? trimmedSearchQuery}”.`
                )}
              </section>
            </div>
          </ScrollArea>
        ) : (
          <Tabs
            value={effectiveActiveTab}
            onValueChange={showTab}
            className="h-full min-w-0 gap-0 overflow-hidden"
          >
            <TabsContent value="categories" className="m-0 h-full">
              <ScrollArea
                className="h-full min-h-0 min-w-0 max-w-full overflow-hidden"
                viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
                viewportStyle={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
              >
                <div className="min-w-0 max-w-full space-y-4 p-4">
                  {!hasVisibleCategoryTiles ? (
                    <div className="flex min-h-[220px] items-center justify-center text-muted-foreground">
                      <Typography variant="body">No quick-add shortcuts found</Typography>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleCategoryDragStart}
                      onDragCancel={clearCategoryDragState}
                      onDragEnd={handleCategoryDragEnd}
                    >
                      <SortableContext
                        items={visibleCategoryGridSlots.flatMap(({ category }) =>
                          category ? [String(category.id)] : []
                        )}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-1 gap-3 @[18rem]:grid-cols-2 @[34rem]:grid-cols-3 @[44rem]:grid-cols-4">
                          {visibleCategoryGridSlots.map(
                            ({ category, slotIndex, isPersistedGap }) => (
                              <CategoryGridSlot
                                key={category?.id ?? getCategorySlotId(slotIndex)}
                                slotIndex={slotIndex}
                                isDraggingCategory={Boolean(activeCategoryDragId)}
                                isPersistedGap={Boolean(isPersistedGap)}
                              >
                                {category ? (
                                  <SortableCategoryTile
                                    category={category}
                                    disabled={disabled}
                                    isSortingActive={Boolean(activeCategoryDragId)}
                                    onAdd={handleCategoryQuickAdd}
                                    onChangeColor={handleCategoryColorChange}
                                  />
                                ) : null}
                              </CategoryGridSlot>
                            )
                          )}
                        </div>
                      </SortableContext>
                      <DragOverlay dropAnimation={null}>
                        {activeCategoryDrag ? (
                          <CategoryTileDragOverlay
                            category={activeCategoryDrag}
                            dragSize={activeCategoryDragSize}
                          />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}

                  <CustomerHistoryQuickAdd
                    cart={cart}
                    disabled={disabled}
                    onSelectHistoryItem={handleHistoryItemClick}
                  />
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="products" className="m-0 h-full min-w-0 overflow-hidden">
              <ScrollArea
                className="h-full min-h-0 min-w-0 max-w-full overflow-hidden"
                viewportRef={setProductScrollViewportElement}
                viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
                viewportStyle={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
              >
                <div
                  data-slot="pos-product-catalog-scroll-content"
                  className="w-full min-w-0 max-w-full space-y-3 overflow-hidden p-4"
                  style={{ width: '100%', maxWidth: '100%' }}
                >
                  <DraggableHorizontalScrollArea>
                    <Button
                      type="button"
                      variant={selectedCollectionId === null ? 'selection' : 'outline'}
                      size="sm"
                      className="shrink-0"
                      onClick={() => setSelectedCollectionId(null)}
                    >
                      All Products
                    </Button>
                    {collections.map((collection) => (
                      <Button
                        key={collection.id}
                        type="button"
                        variant={selectedCollectionId === collection.id ? 'selection' : 'outline'}
                        size="sm"
                        className="shrink-0"
                        onClick={() => setSelectedCollectionId(collection.id)}
                      >
                        {collection.name}
                      </Button>
                    ))}
                  </DraggableHorizontalScrollArea>

                  {renderProductResults('No products found')}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>

      <CardFooter className="shrink-0 border-t px-4 py-2">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Typography variant="label" tone="muted" className="min-w-0">
            Use shortcuts to quick-add products to cart, or choose from all website products when
            needed.
          </Typography>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setQuickAddCategoryIdForPriceMemory(null)
              setCustomItemDefaults(null)
              setIsCustomItemModalOpen(true)
            }}
            disabled={disabled}
            className="w-auto shrink-0 self-start px-4 has-[>svg]:px-3 sm:self-auto"
          >
            <Plus className="h-4 w-4" />
            Add Custom Item
          </Button>
        </div>
      </CardFooter>

      <VariantSelectionModal
        open={isVariantModalOpen && Boolean(selectedProduct)}
        onOpenChange={(isOpen) => {
          setIsVariantModalOpen(isOpen)

          if (!isOpen) {
            setSelectedProductId(null)
          }
        }}
        product={selectedProduct}
        quantityByVariantId={quantityByVariantId}
        onSelectVariant={handleVariantSelected}
      />

      <CustomItemModal
        open={isCustomItemModalOpen}
        onOpenChange={handleCustomItemModalOpenChange}
        cart={cart}
        cartId={persistedCartId}
        createCartWithDefaults={createCartWithDefaults}
        disabled={disabled}
        initialValues={customItemDefaults}
        focusPriceOnOpen={Boolean(customItemDefaults)}
        onCustomItemAdded={handleCustomItemAdded}
      />
    </Card>
  )
}
